<template>
    <div>
        <div class="stellar" ref="d3_root"></div>
        <div v-if="chartData && (!render_info || render_info.display_extra)">
            <span style="color:lightgray">
                Stellar
            </span>
        </div>
    </div>
</template>

<script>
    import * as d3 from "d3";
    export default {
        data() {
            return {
                categories: [],
                categories_for_display: [],
                category_name: '',
                hover_category: null,
            }
        },
        mounted() {
            if (this.chartData) {
                this.draw()
            }
        },
        computed: {
            labels: function () {
                if (!this.chartData) {
                    return {
                        level1: '',
                        level2: '',
                        level3: ''
                    }
                } else {
                    let meta = this.chartData.meta
                    return {
                        level1: meta.level1,
                        level2: meta.level2,
                        level3: meta.level3
                    }
                }
            }
        },
        watch: {
            highlighted: function (newValue,oldValue) {
              if (newValue !== oldValue) this.draw()
            },
            chartData: function (newValue) {
                if (newValue) this.draw()
            },
            height: function(newValue) {
                if (newValue) this.draw()
            },
            text_size: function(newValue) {
                if (newValue) this.draw()
            },
            hover_category: function(newValue) {
                this.draw()
            }
        },
        props: ['height','width', 'text_size', 'chartData','highlighted','render_info'],
        methods: {
            draw() {
                function commarize(number) {
                    let min = 1e3;
                    // Alter numbers larger than 1k
                    if (number >= min) {
                        var units = ["k", "M", "B", "T", "Q"];

                        var order = Math.floor(Math.log(number) / Math.log(1000));

                        var unitname = units[(order - 1)];
                        var num = Math.floor(number / 1000 ** order);

                        // output number remainder + unitname
                        return num + unitname
                    }

                    // return formatted original number
                    return number.toLocaleString()
                }

                const svg_element = document.createElementNS("http://www.w3.org/2000/svg",'svg')

                let margin_for_labels = 100

                if (this.render_info && !this.render_info.display_extra) {
                    margin_for_labels = 35
                }


                let main_chart_width = this.width - margin_for_labels
                let main_chart_height = this.height - margin_for_labels

                let diameter = main_chart_height > main_chart_width ? main_chart_width : main_chart_height
                var radius = diameter / 2;
                var midpoint_offset = this.width / 2 + margin_for_labels / 2
                var radius_offset = radius + margin_for_labels / 2

                const svg = d3.select(svg_element)
                    .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                ;
                svg.append('path')
                    .attr("id", "axis1")
                    .attr("transform",`translate(${midpoint_offset},${radius_offset})`)
                svg.append('path')
                    .attr("id", "axis2")
                    .attr("transform",`translate(${midpoint_offset},${radius_offset})`)
                svg.append('path')
                    .attr("id", "axis3")
                    .attr("transform",`translate(${midpoint_offset},${radius_offset})`)
                svg.append('path')
                    .attr("id", "axis4")
                    .attr("transform",`translate(${midpoint_offset},${radius_offset})`)

                svg.append('path')
                    .attr('id','stellar_path')
                    .attr("transform",`translate(${midpoint_offset},${radius_offset})`)

                svg.append('g')
                    .attr("transform",`translate(${midpoint_offset},${radius_offset})`)

                while (this.$refs.d3_root.firstChild) {
                    this.$refs.d3_root.removeChild(this.$refs.d3_root.firstChild);
                }


                let inner_r = 0.1 * radius;     // inner radius (constant)
                if (inner_r < 10) inner_r = 10
                let outer_r = radius; // OUTER radius (maximum)
                let range = outer_r - inner_r
                console.log('here comes the range')
                console.log(range)

                let radialLineGenerator = d3.radialLine();

                let chartRawPoints = this.chartData.datasets[0].data

                let max = chartRawPoints.reduce((a, b) => Math.max(a, b), -Infinity);

                let axisRadialY = [
                    Math.ceil((range/4) + inner_r),
                    Math.ceil((range/2) + inner_r),
                    Math.ceil((range * 0.75) + inner_r),
                    Math.ceil(range + inner_r)
                ]

                console.log('here comes the axes')
                console.log(axisRadialY)

                let axisRawPoints = [
                    [],
                    [],
                    [],
                    []
                ]

                let r1_multiplier = range / max

                let nominalAngle = 0

                let radialPoints = []

                let angleIncrement = Math.PI  / chartRawPoints.length
                let baseAngle =  angleIncrement * 0.9
                let minBaseAngle = 2 * Math.PI / 40
                let maxBaseAngle = 2 * Math.PI / 5
                if (baseAngle < minBaseAngle) {
                    baseAngle = minBaseAngle
                    console.log('using minBaseAngle')
                }
                if (baseAngle > maxBaseAngle) {
                    baseAngle = maxBaseAngle
                }

                let is_blunt = chartRawPoints.length >= 20 ? false : true

                let text_g = svg.select("g")

                text_g.append('circle').attr('r',inner_r).attr('stroke','#e31a1c').attr('fill','#e31a1c')
                text_g.append('circle').attr('r',inner_r*0.75).attr('stroke','#fffff').attr('fill','#ffffff')

                let firstPoint = false

                let label_index = 0
                let arcLength = 3

                chartRawPoints.forEach(dataPoint => {
                    let radius_data = r1_multiplier * dataPoint + inner_r
                    let circumference_at_data_point = 2 * Math.PI * radius_data
                    // Arc angle = (arc length / C) * 360
                    let arcAngle = (arcLength / circumference_at_data_point) * 2 * Math.PI
                    if (!is_blunt) {
                        arcAngle = 0
                    }
                    // counter-clockwise point of blunt point
                    radialPoints.push([nominalAngle - arcAngle / 2,radius_data])
                    if (!firstPoint) {
                        let firstPoint = [nominalAngle - arcAngle / 2,radius_data]
                    }
                    // clockwise point of blunt point
                    radialPoints.push([nominalAngle + arcAngle / 2,radius_data])
                    let x_label = (radius + margin_for_labels / 4) * Math.cos(nominalAngle - Math.PI / 2);
                    let y_label = (radius + margin_for_labels / 4) * Math.sin(nominalAngle - Math.PI / 2);

                    let font_size = ''
                    if (this.render_info && !this.render_info.display_extra) {
                        font_size = 'smaller'
                    }

                    let label = text_g.append('text')
                        .text(this.chartData.labels[label_index])
                        .attr('x', x_label)
                        .attr('y', y_label)
                        .attr("text-anchor", "middle")
                        .attr('font-size',font_size);
                    if (!this.render_info || this.render_info.display_extra) {

                        let x_size = (radius_data + 10) * Math.cos(nominalAngle - Math.PI / 2)
                        let y_size = (radius_data + 10) * Math.sin(nominalAngle - Math.PI / 2)
                        text_g.append('text')
                            .text(commarize(this.chartData.datasets[0].data[label_index]))
                            .attr('x', x_size)
                            .attr('y', y_size)
                            .attr("text-anchor", "middle");
                    }

                    label_index++
                    // axes points
                    let i = 0
                    axisRawPoints.forEach(axis => {
                        axis.push([nominalAngle,axisRadialY[i++]])
                    })
                    console.log(axisRawPoints)

                    // now draw the points back at the inner circle
                    radialPoints.push([nominalAngle+baseAngle/2, inner_r])
                    nominalAngle += angleIncrement
                    nominalAngle += angleIncrement
                    radialPoints.push([nominalAngle-baseAngle/2, inner_r])
                })

                let i = 0

                radialPoints.push(firstPoint)
                axisRawPoints.forEach(axis => {
                    axis.push([0,axisRadialY[i++]])
                })

                let radialData = radialLineGenerator(radialPoints);
                svg.select("#stellar_path")
                    .attr("class", "radial")
                    .attr('stroke','#e31a1c').attr('fill','#e31a1c')
                    .attr('stroke-linejoin','round')
                    .attr('rx','5px')
                    .attr('ry','5px')
                    .attr("d", radialData);
                svg.select("#axis1").attr("class", "radial").attr('stroke','#dddddd').attr('fill','none').attr("d", radialLineGenerator(axisRawPoints[0]));
                svg.select("#axis2").attr("class", "radial").attr('stroke','#dddddd').attr('fill','none').attr("d", radialLineGenerator(axisRawPoints[1]));
                svg.select("#axis3").attr("class", "radial").attr('stroke','#dddddd').attr('fill','none').attr("d", radialLineGenerator(axisRawPoints[2]));
                svg.select("#axis4").attr("class", "radial").attr('stroke','#dddddd').attr('fill','none').attr("d", radialLineGenerator(axisRawPoints[3]));



                this.$refs.d3_root.append(svg.node())

            },
            removeElement(elementId) {
                // Removes an element from the document
                var element = document.getElementById(elementId);
                element.parentNode.removeChild(element);
            }
        }
    }
</script>